import { describeError } from '@kdk/shared';
import { Alert, Button, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import type { AsyncState } from './asyncState';
import { LoadingStatus } from './LoadingStatus';
import { BlockSkeleton } from './skeletons';

type Props<T> = {
  state: AsyncState<T>;
  isEmpty?: (data: T) => boolean;
  emptyMessage?: string;
  /** 読み込み中に出すスケルトン。表示する中身と同じ枠・同じ高さにして、画面がずれないようにする。既定は単純な棒。 */
  skeleton?: ReactNode;
  /**
   * 親が `fillPageSx(from)` の縦の flex 列のとき、読み込み中の領域に残りの高さを受け取らせる
   * (中身の `TableScroll fill` と同じ振る舞い)。`from` より狭い画面では通常の流れ。
   */
  fillFrom?: 'sm' | 'md';
  children: (data: T) => ReactNode;
};

export const AsyncBoundary = <T,>({
  state,
  isEmpty,
  emptyMessage = 'データがありません',
  skeleton,
  fillFrom,
  children,
}: Props<T>) => {
  if (state.status === 'pending') {
    return (
      <LoadingStatus
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          ...(fillFrom !== undefined && {
            minHeight: 0,
            flex: { xs: 'none', [fillFrom]: '1 1 0' },
          }),
        }}
      >
        {skeleton ?? <BlockSkeleton />}
      </LoadingStatus>
    );
  }
  if (state.status === 'error') {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={state.retry}>
            再試行
          </Button>
        }
      >
        {describeError(state.error)}
      </Alert>
    );
  }
  if (isEmpty?.(state.data) === true) {
    return (
      <Typography role="note" color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
        {emptyMessage}
      </Typography>
    );
  }
  return <>{children(state.data)}</>;
};
